import { useEffect, useState } from "react";
import { Alert, Box, Card, CardContent, Chip, Typography } from "@mui/material";
import api from "../api";
import ResourceDataView from "./shared/ResourceDataView";

export default function RuntimeStatus() {
  const [data, setData] = useState<any>(null),
    [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const response = await api.get("/infra/runtime");
        if (alive) {
          setData(response.data);
          setError("");
        }
      } catch {
        if (alive) setError("Não foi possível atualizar a carga do executor.");
      } finally {
        if (alive) timer = setTimeout(refresh, 15000);
      }
    };
    void refresh();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, []);
  const pool = data?.pool;
  const seconds = (ms: number | undefined) =>
    ((ms || 0) / 1000).toFixed(1) + " s";
  const tenants = (pool?.tenants || []).map((row: any) => ({
    ...row,
    _id: row.alias,
  }));
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Carga e filas por instância
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Processos isolados por tarefa. Limite global de execução, distribuição
          entre clientes e capacidade reservada para PDFs e solicitações da
          tela.
        </Typography>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
            {data ? " Os dados abaixo são da última atualização." : ""}
          </Alert>
        )}
        {pool && (
          <>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              <Chip
                label={`Executando: ${pool.active}/${pool.capacity}`}
                color="primary"
              />
              <Chip label={`Aguardando executor: ${pool.pending}`} />
              <Chip label={`Fundo: até ${pool.backgroundCapacity}`} />
              <Chip label={`Reserva interativa: ${pool.interactiveReserve}`} />
            </Box>
            <ResourceDataView
              resourceName="Carga por instância"
              items={tenants}
              columns={[
                { header: "Instância", accessor: (row: any) => row.alias },
                {
                  header: "Estado",
                  accessor: (row: any) =>
                    row.active
                      ? "Executando"
                      : row.pending
                      ? "Aguardando"
                      : "Livre",
                },
                { header: "Pendentes", accessor: (row: any) => row.pending },
                {
                  header: "Concluídas / falhas",
                  accessor: (row: any) =>
                    `${row.completed || 0} / ${row.failed || 0}`,
                },
                {
                  header: "Espera média",
                  accessor: (row: any) =>
                    seconds(row.totalWaitMs / Math.max(1, row.started || 0)),
                },
                {
                  header: "Duração média",
                  accessor: (row: any) =>
                    seconds(
                      row.totalRunMs /
                        Math.max(1, (row.completed || 0) + (row.failed || 0))
                    ),
                },
                {
                  header: "Pico de memória Node",
                  accessor: (row: any) =>
                    `${Math.round((row.peakRssBytes || 0) / 1048576)} MiB`,
                },
                {
                  header: "CPU acumulada Node",
                  accessor: (row: any) => seconds(row.cpuMs),
                },
              ]}
            />
            <Typography
              variant="caption"
              component="p"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              Contadores desde o início do worker. Memória e CPU referem-se ao
              processo Node executor; navegadores e demais serviços são
              adicionais. Atualizado:{" "}
              {new Date(data.sampledAt).toLocaleString("pt-BR")}.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <ResourceDataView
                resourceName="Filas"
                items={(data.queues || []).map((row: any) => ({
                  ...row,
                  _id: row.name,
                }))}
                columns={[
                  { header: "Fila", accessor: (row: any) => row.name },
                  { header: "Aguardando", accessor: (row: any) => row.wait },
                  { header: "Ativas", accessor: (row: any) => row.active },
                  {
                    header: "Retentativa / adiadas",
                    accessor: (row: any) => row.delayed,
                  },
                  {
                    header: "Falhas retidas",
                    accessor: (row: any) => row.failed,
                  },
                ]}
              />
            </Box>
          </>
        )}
        {!pool && !error && (
          <Typography color="text.secondary">Consultando carga…</Typography>
        )}
      </CardContent>
    </Card>
  );
}
